import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  ExternalLink,
  Clock,
} from 'lucide-react';
import { newsService } from '../../services/api/newsService';
import { News } from '../../types/news.types';
import { toast } from 'react-toastify';
import { resolveDocUrl } from './NewsForm';

const NewsView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<News | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchNews(parseInt(id));
    }
  }, [id]);

  const fetchNews = async (newsId: number) => {
    try {
      setLoading(true);
      const response = await newsService.getById(newsId);
      if (response.success) {
        setNews(response.data);
      } else {
        toast.error('Failed to fetch news details');
        navigate('/news');
      }
    } catch (error) {
      toast.error('Failed to fetch news details');
      console.error('Error fetching news:', error);
      navigate('/news');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | undefined): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string): string => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const StatusBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => (
    <span
      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
      }`}
    >
      {isActive ? (
        <>
          <CheckCircle className="w-4 h-4 mr-1.5" />
          Active
        </>
      ) : (
        <>
          <XCircle className="w-4 h-4 mr-1.5" />
          Inactive
        </>
      )}
    </span>
  );

  const InfoSection: React.FC<{
    label: string;
    value: string | React.ReactNode;
    icon?: React.ReactNode;
    fullWidth?: boolean;
  }> = ({ label, value, icon, fullWidth = false }) => (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <dt className="flex items-center text-sm font-medium text-gray-500 mb-2">
        {icon && <span className="mr-2 text-gray-400">{icon}</span>}
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading news details...</p>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <FileText size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">News not found</h3>
          <p className="text-gray-600 mb-4">
            The news item you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/news')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">News Details</h1>
          <p className="mt-1 text-sm text-gray-600">
            View complete information about this news item
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/news/edit/${id}`}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors"
          >
            <Edit size={18} className="mr-2" />
            Edit
          </Link>
          <button
            onClick={() => navigate('/news')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {/* Header Section */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {news.go_number}
              </h2>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar size={16} className="mr-2" />
                {formatDate(news.date)}
              </div>
            </div>
            <StatusBadge isActive={news.is_active} />
          </div>
        </div>

        {/* Details Section */}
        <div className="px-6 py-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* G.O. Number */}
            <InfoSection
              label="G.O. Number"
              value={news.go_number}
              icon={<FileText className="w-4 h-4" />}
            />

            {/* Date */}
            <InfoSection
              label="Date"
              value={formatDate(news.date)}
              icon={<Calendar className="w-4 h-4" />}
            />

            {/* Description */}
            <InfoSection
              label="Description"
              value={
                <p className="whitespace-pre-wrap leading-relaxed">
                  {news.description}
                </p>
              }
              fullWidth
            />

            {/* Document URL */}
            <InfoSection
              label="Document URL"
              icon={<FileText className="w-4 h-4" />}
              value={
                news.document_url ? (
                  <a
                    href={resolveDocUrl(news.document_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline break-all"
                  >
                    <span className="mr-2">{news.document_url.split('/').pop()}</span>
                    <ExternalLink size={14} className="flex-shrink-0" />
                  </a>
                ) : (
                  <span className="text-gray-400">No document attached</span>
                )
              }
              fullWidth
            />

            {/* Display Order */}
            <InfoSection label="Display Order" value={news.display_order.toString()} />

            {/* Status */}
            <InfoSection
              label="Status"
              value={
                news.is_active ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active - Visible on website
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Inactive - Hidden from website
                  </span>
                )
              }
            />
          </dl>
        </div>

        {/* Metadata Section */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Clock size={16} className="mr-2" />
            Metadata
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500 mb-1">Created At</dt>
              <dd className="text-gray-900">{formatDateTime(news.created_at)}</dd>
            </div>
            <div>
              <dt className="text-gray-500 mb-1">Last Updated</dt>
              <dd className="text-gray-900">{formatDateTime(news.updated_at)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Action Buttons (Mobile-friendly) */}
      <div className="flex flex-col sm:flex-row gap-3 sm:hidden">
        <Link
          to={`/news/edit/${id}`}
          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors"
        >
          <Edit size={18} className="mr-2" />
          Edit News
        </Link>
        <button
          onClick={() => navigate('/news')}
          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to List
        </button>
      </div>
    </div>
  );
};

export default NewsView;